/**
 * ViewerBackend — the one interface the UI / Redux actions call.
 *
 * The unified viewer must speak three different contracts:
 *   - local NVR  → rda-backend JSON-RPC over POST /JSON/   (LocalNvrAdapter)
 *   - cloud NVR  → cloudapi `/ve/*` REST + JWT             (CloudNvrAdapter, Chunk 3)
 *   - EEN        → cloudapi `/ve/een/*` REST + JWT          (EenAdapter, Chunk 5)
 *
 * Because local is RPC and cloud/EEN are REST, the seam CANNOT be a
 * parameterized URL — it has to be defined by *capability*. Each adapter maps
 * a capability (e.g. `listCameras()`) onto its own transport. This base class
 * is the contract; adapters override what they support. Capabilities a given
 * backend lacks should be FEATURE-GATED by the caller (check a `supports()`
 * flag), not discovered by catching a thrown error — but until a method is
 * implemented anywhere, the default throw is a loud dev guardrail.
 *
 * Method → local JSON-RPC mapping is documented per method so the
 * LocalNvrAdapter migration (collapsing the 42 inline axios calls) is
 * mechanical and auditable.
 *
 * This file intentionally contains NO transport code — see the adapters.
 */
export class ViewerBackend {
  /** Machine-readable backend kind: 'local' | 'cloud' | 'een'. */
  get kind() {
    return 'abstract';
  }

  _notImplemented(name) {
    throw new Error(`[${this.constructor.name}] ${name}() is not implemented`);
  }

  // ---- auth / session ----------------------------------------------------
  /** loginToServer flow → auth.loginUser / auth.loginUserEnterprise. */
  login(/* { server, username, password, opts } */) { this._notImplemented('login'); }
  /** auth.logoutUser / auth.logoutUserEnterprise. */
  logout(/* sSess, opts */) { this._notImplemented('logout'); }
  /** Liveness probe → info.isAlive. */
  isAlive(/* opts */) { this._notImplemented('isAlive'); }
  /** Identity probe used by whichIP → info.getSerial. */
  getSerial(/* opts */) { this._notImplemented('getSerial'); }

  // ---- identity / capability --------------------------------------------
  /** Startup blob + feature flags → config.meta.getStartup. */
  getCapabilities(/* sSess, opts */) { this._notImplemented('getCapabilities'); }
  /** This NVR's own identity (serial/name/timezone) → config.meta.getStartup / info.getSerial. */
  getServerIdentity(/* sSess, opts */) { this._notImplemented('getServerIdentity'); }
  /** eView member-server list → config.server.getAllAuthServers. */
  listMemberServers(/* sSess, sName, opts */) { this._notImplemented('listMemberServers'); }

  // ---- cameras / views ---------------------------------------------------
  /** config.camera.getAllCameras (this NVR) / config.user.getCameras (per-user). */
  listCameras(/* sSess, opts */) { this._notImplemented('listCameras'); }
  /** User view-sets → config.user.getSets. */
  listViews(/* sSess, bUserID, bSerial, opts */) { this._notImplemented('listViews'); }
  /** config.user.addSet. */
  saveView(/* sSess, view, opts */) { this._notImplemented('saveView'); }
  /** config.user.delSet. */
  deleteView(/* sSess, viewId, opts */) { this._notImplemented('deleteView'); }
  /** Per-user ACL mask → config.user.getRights. */
  getUserRights(/* sSess, bUserID, bSerial, opts */) { this._notImplemented('getUserRights'); }
  /** Looked-up user record → config.user.getUserByName. */
  getUserByName(/* sSess, sName, opts */) { this._notImplemented('getUserByName'); }

  // ---- events / playback -------------------------------------------------
  /** Recorded-event paging → event.getEvents (timestamp-cursor tuple). */
  listEvents(/* sSess, query, opts */) { this._notImplemented('listEvents'); }
  /** Mark/unmark an event for export → event flag RPC. */
  setExportFlag(/* sSess, eventId, flag, opts */) { this._notImplemented('setExportFlag'); }

  // ---- PTZ ---------------------------------------------------------------
  /** config.ptz.getAllPresets / config.ptz.getAllConfigs (display-only in lite). */
  listPtzPresets(/* sSess, opts */) { this._notImplemented('listPtzPresets'); }

  // ---- setup (Chunk 4 surface; mostly local-only today) ------------------
  /** config.system.getAll. */
  getSystemConfig(/* sSess, opts */) { this._notImplemented('getSystemConfig'); }
  /** config.dtime.getAll. */
  getDateTimeConfig(/* sSess, opts */) { this._notImplemented('getDateTimeConfig'); }

  // ---- setup: users (admin) ---------------------------------------------
  // Cloud → /ve (gated). Local → config.user.* when the local Setup port lands.
  /** All users + org (customers/projects) for the Users screen → GET /ve/users. */
  getUsers(/* opts */) { this._notImplemented('getUsers'); }
  /** Create/update a user (role + rights + site access) → POST /ve/user (gated). */
  saveUser(/* user, opts */) { this._notImplemented('saveUser'); }
  /** Delete a user → POST /ve/user { id, del:1 } (gated; permanent hard delete). */
  deleteUser(/* id, opts */) { this._notImplemented('deleteUser'); }
  /** Enable/disable a user (reversible; blocks login) → POST /ve/user/disable (gated). */
  setUserDisabled(/* id, fDisabled, opts */) { this._notImplemented('setUserDisabled'); }
  /** Reset a user's password (backend emails it) → POST /ve/resetpassword (gated). */
  resetUserPassword(/* id, opts */) { this._notImplemented('resetUserPassword'); }

  // ---- setup: org — customers / sites / devices (cloud v1) ---------------
  // Cloud → /ve (gated). Local has no cloud org hierarchy, so these cloud-only org methods are never
  // reached in the local adapter (the interface documents the contract).
  /** All customers → GET /ve/customers (master sees all; else own). */
  getCustomers(/* opts */) { this._notImplemented('getCustomers'); }
  /** Create/update a customer → POST /ve/customer (master-only, gated). */
  saveCustomer(/* customer, opts */) { this._notImplemented('saveCustomer'); }
  /** Delete a customer → POST /ve/customer { id, del:1 } (master-only; cascades). */
  deleteCustomer(/* id, opts */) { this._notImplemented('deleteCustomer'); }
  /** Customers + sites → GET /ve/projects ("project" = site). */
  getProjects(/* opts */) { this._notImplemented('getProjects'); }
  /** Create/update a site → POST /ve/project (admin + org-scope gated). */
  saveProject(/* project, opts */) { this._notImplemented('saveProject'); }
  /** Delete a site → POST /ve/project { id, del:1 } (gated; unassigns devices). */
  deleteProject(/* id, opts */) { this._notImplemented('deleteProject'); }
  /** Customers + sites + devices → GET /ve/devices (each device carries parsed sConfig + org). */
  getDevices(/* opts */) { this._notImplemented('getDevices'); }
  /** Update a device's org assignment (name/customer/site), round-tripping config → POST /ve/device. */
  saveDevice(/* device, opts */) { this._notImplemented('saveDevice'); }

  // ---- setup: cameras + device config (cloud v1) -------------------------
  // Cloud → /ve (gated). Local → config.camera.* when the local Setup port lands.
  /** All cameras + org (devices/customers/projects) for the Cameras screen → GET /ve/cameras. */
  getCameras(/* opts */) { this._notImplemented('getCameras'); }
  /** Parsed device config (the INI blob) for one device → GET /ve/cameras|devices. */
  getDeviceConfig(/* deviceId, opts */) { this._notImplemented('getDeviceConfig'); }
  /** Write a device's full config blob → POST /ve/device (identity preserved, gated). */
  setDeviceConfig(/* deviceId, config, opts */) { this._notImplemented('setDeviceConfig'); }
  /** Save one camera: friendly name (sName) + patched device config → POST /ve/camera (gated). */
  saveCamera(/* cam, opts */) { this._notImplemented('saveCamera'); }
  /** PTZ presets for a device camera → GET /ve/getptzpresets/:camera. */
  getPtzPresets(/* deviceId, cam, opts */) { this._notImplemented('getPtzPresets'); }
  /** Create/rename a PTZ preset → POST /ve/setptzpreset (gated). */
  savePtzPreset(/* deviceId, cam, presetNum, name, opts */) { this._notImplemented('savePtzPreset'); }
  /** Remove a PTZ preset → GET /ve/removeptzpreset/:camera/:preset. */
  removePtzPreset(/* deviceId, cam, presetNum, opts */) { this._notImplemented('removePtzPreset'); }

  // ---- stream URL builders (Seam B — polymorphic per backend) ------------
  // These return URLs (no network call); each adapter encapsulates its own
  // transport/auth. The local adapter delegates to util/getServerInfo.
  /** Live stream URL (WebSocket) for a camera on THIS server. */
  buildLiveUrl(/* cameraId, profile, auth, options */) { this._notImplemented('buildLiveUrl'); }
  /** Live stream URL for a camera on a specific member NVR (eView). */
  buildEviewLiveUrl(/* camera, profile, serverSessions, fallbackAuth */) { this._notImplemented('buildEviewLiveUrl'); }
  /** JPEG snapshot URL for an eView member camera. */
  buildEviewJpegUrl(/* camera, isHighRes, serverSessions, fallbackAuth */) { this._notImplemented('buildEviewJpegUrl'); }
  /** Generic resource URL on this server (e.g. /mpe/cam1.jpg). */
  buildResourceUrl(/* path */) { this._notImplemented('buildResourceUrl'); }
  /** Recorded-clip stream/download URL (local: direct pbserver HTTP). */
  buildClipUrl(/* clip, opts */) { this._notImplemented('buildClipUrl'); }
}
